import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import AdminTable from "components/admin/AdminTable";
import { useConfirm } from "components/admin/Feedback";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatDate, formatNumber } from "utils/format";

/** VIP của một tài khoản (docs/design/99-vip.md mục 9.5): bậc, điểm VIP, quà lên cấp đã gửi, gửi lại quà. */
function PlayerVipTab({ userId }) {
  const canEdit = useCan(PERMISSIONS.VIP_EDIT);
  const data = useFetch(() => api.get(`/api/admin/vip/player/${userId}`), [userId]);
  const [run] = useRunner();
  const confirm = useConfirm();
  const v = data.data;

  const resend = async (level) => {
    const ok = await confirm({
      title: `Gửi lại quà lên VIP ${level}?`,
      message:
        "Xoá dấu đã nhận của bậc này rồi gửi một thư quà mới. Chỉ dùng khi thư cũ bị mất (server sập giữa lúc gửi, xoá nhầm thư).",
      confirmText: "Gửi lại",
      color: "warning",
    });
    if (!ok) return;
    const { ok: done } = await run(
      () => api.post(`/api/admin/vip/player/${userId}/resend/${level}`),
      (t) => t
    );
    if (done) data.reload(true);
  };

  return (
    <MDBox p={2}>
      {data.error && <Alert severity="error">{data.error}</Alert>}
      {v && (
        <MDBox mb={2}>
          <MDTypography variant="h6">
            {v.level > 0 ? `VIP ${v.level} · ${v.groupName}` : "Chưa có VIP"}
          </MDTypography>
          <MDTypography variant="button" display="block">
            Điểm VIP (tổng nạp): {formatNumber(v.total)} Pet Coin (≈ {formatNumber(v.total * 100)}đ)
          </MDTypography>
          {v.computedLevel !== v.level && (
            <MDTypography variant="caption" color="warning" display="block">
              Theo ngưỡng hiện tại tổng nạp ứng với VIP {v.computedLevel}. Bậc đã đạt không giảm;
              bậc cao hơn sẽ được tính ở lần nạp kế tiếp hoặc lúc đăng nhập.
            </MDTypography>
          )}
          <MDTypography variant="caption" display="block">
            Quà tuần gần nhất: {v.lastWeek || "chưa gửi"}
          </MDTypography>
        </MDBox>
      )}
      <AdminTable
        loading={data.loading}
        rows={v?.claims || []}
        rowKey="level"
        emptyText="Chưa nhận quà lên cấp nào"
        columns={[
          { key: "level", label: "Bậc", render: (r) => `VIP ${r.level}` },
          {
            key: "mailId",
            label: "Thư",
            render: (r) => (r.mailId > 0 ? `#${r.mailId}` : "Đang gửi"),
          },
          { key: "createdAt", label: "Lúc", render: (r) => formatDate(r.createdAt) },
          canEdit && {
            key: "actions",
            label: "",
            align: "right",
            render: (r) => (
              <MDButton size="small" variant="text" color="warning" onClick={() => resend(r.level)}>
                <Icon>forward_to_inbox</Icon>&nbsp;Gửi lại
              </MDButton>
            ),
          },
        ].filter(Boolean)}
      />
    </MDBox>
  );
}

PlayerVipTab.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default PlayerVipTab;
